import React, { useCallback, useContext, useMemo, useState } from "react";
import { Dialog, Button } from "@/components/ui";
import { Send, Share2 } from "lucide-react";
import copy from "copy-to-clipboard";
import { WebComponents } from "@/interfaces";
import { SuperPageServices } from "@/services/bedrock";
import { ContentPageServices } from "@/services/bedrock";

import EditVideoPageCard from "./EditVideoPageCard";
import { VideoPagePreview, VideoPageStatsBadges } from "@/containers";
import { URLS, useAccountSettings, useSocialCoachSession } from "@/utils";
import { v4 as uuidv4 } from "uuid";
import { confirmDialog } from "@/components/ui";
import { toast } from "sonner";
import { AppContext } from "@/providers/AppContextProvider";
import { ContentPage, Media } from "@/services/bedrock";
import { VideoPageShareDialog } from "./VideoPageShareDialog";

interface OwnProps {
  videoPage: ContentPage;
  showStats?: boolean;

  onVideoPageUpdated: (videoPage: ContentPage) => void;
  onVideoPageDeleted: (videoPage: ContentPage) => void;
  onOpenPostDialog: (media: Media) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  autoOpenShare?: boolean;
  onDone?: () => void;
}

type Props = OwnProps;

export const VideoPageModalContent: React.FC<Props> = ({
  videoPage,
  showStats = false,
  onVideoPageUpdated,
  onVideoPageDeleted,
  onOpenPostDialog,
  onDirtyChange,
  autoOpenShare,
  onDone,
}) => {
  const { playerId, isCoach, isPlayer } = useSocialCoachSession();
  const { userContext } = useContext(AppContext);
  const playerEmail = userContext?.account?.email;
  const [showShareDialog, setShowShareDialog] = useState<boolean>(!!autoOpenShare);

  const { hasWritePermission } = useAccountSettings();

  const withWritePermission = useMemo(() => {
    return isPlayer ? videoPage?.playerId === playerId : hasWritePermission(WebComponents.VIDEO_PAGES);
  }, [isPlayer, playerId, videoPage?.playerId, hasWritePermission]);

  const [refreshKey, setRefreshKey] = useState<string>(uuidv4());

  const markAsShared = async () => {
    if (!videoPage || !withWritePermission) return;
    try {
      await ContentPageServices.updateContentPage(videoPage.id, { markAsShared: true });
    } catch (e) {
      console.error("Error marking as shared", e);
    }
  };

  const videoPageUrl = useMemo(() => {
    if (!videoPage) return undefined;
    return videoPage.url;
  }, [videoPage]);

  const thumbnailUrl = useMemo(() => {
    if (!videoPage) return undefined;
    var url = new URL(videoPage.url + ".png");
    if (isPlayer && videoPage.superPageId && playerEmail) {
      url.searchParams.set("p", playerEmail);
    }
    return url.toString();
  }, [videoPage?.url, isPlayer, playerEmail, videoPage?.superPageId]);

  const primaryMedia = useMemo(() => {
    return videoPage?.originalMedia?.[0];
  }, [videoPage?.originalMedia]);

  const showPostButton = !!primaryMedia && !videoPage?.formRequestId && !videoPage?.reviewId;

  const isHorizontalVideo = useMemo(() => {
    return primaryMedia?.width && primaryMedia?.height && primaryMedia?.height <= primaryMedia?.width;
  }, [primaryMedia?.width, primaryMedia?.height]);

  const superPageComponent = useMemo(() => {
    const { id, superPageId, playerId } = videoPage || {};

    if (!isCoach || !id) return null;

    const goToSuperPage = (superPageId: number) => {
      window.open(URLS.coach.superPage.replace(":id", superPageId.toString()));
    };

    if (superPageId) {
      return (
        <Button variant="outline" onClick={() => goToSuperPage(superPageId)}>
          Go to Super Page
        </Button>
      );
    } else if (!playerId && withWritePermission) {
      const convertToSuperPage = () => {
        confirmDialog({
          title: "Convert to Super Page",
          description: "Are you sure you want to convert this Video Page to a Super Page?",
        }).then(result => {
          if (result) {
            SuperPageServices.convertToSuperPage(id)
              .then(superPage => {
                goToSuperPage(superPage.id);
              })
              .catch(error => {
                console.error(error);
                toast.error("Error converting to Super Page");
              });
          }
        });
      };
      return (
        <Button variant="outline" onClick={convertToSuperPage}>
          Convert to Super Page
        </Button>
      );
    }
    return null;
  }, [videoPage, isCoach, withWritePermission]);

  const handlePreviewClick = useCallback(() => {
    if (!videoPage) return;

    markAsShared();
    window.open(videoPage.url, "_blank");
  }, [videoPage]);

  const handleVideoPageUpdated = useCallback(
    (newVideoPage: ContentPage) => {
      onVideoPageUpdated(newVideoPage);
      setRefreshKey(uuidv4());
    },
    [onVideoPageUpdated]
  );

  return (
    <>
      <Dialog.Content className="p-5 flex flex-row gap-4 max-h-[calc(100vh-12rem)] overflow-y-auto">
        <div className="flex flex-col gap-2 min-w-1/3">
          <VideoPagePreview
            slug={videoPage.slug}
            email={playerEmail}
            refreshKey={refreshKey}
            onClick={handlePreviewClick}
            className="flex-grow"
          />
          {showStats && <VideoPageStatsBadges videoPage={videoPage} className="flex-shrink" />}
        </div>
        <div className="flex-grow">
          <EditVideoPageCard
            videoPage={videoPage}
            readOnly={!withWritePermission}
            onDeleted={onVideoPageDeleted}
            onUpdate={handleVideoPageUpdated}
            onDirtyChange={onDirtyChange}
          />
        </div>
      </Dialog.Content>
      <Dialog.Footer className="flex items-center gap-2">
        {superPageComponent}
        <div className="flex-grow" />
        {showPostButton && (
          <Button
            variant="ghost"
            size="lg"
            onClick={() => onOpenPostDialog(primaryMedia)}
            className="gap-2 text-muted-foreground hover:text-foreground font-semibold"
          >
            <Send className="size-4" />
            Post to Socials
          </Button>
        )}
        <Button size="lg" onClick={() => setShowShareDialog(true)} className="gap-2 font-semibold">
          <Share2 className="size-4" />
          Share Video Page
        </Button>
      </Dialog.Footer>

      {showShareDialog && (
        <VideoPageShareDialog
          open={showShareDialog}
          onDone={() => {
            setShowShareDialog(false);
            onDone?.();
          }}
          onOpenChange={setShowShareDialog}
          videoPageUrl={videoPageUrl || ""}
          thumbnailUrl={thumbnailUrl || ""}
          videoPageTitle={videoPage.title || ""}
          isHorizontalVideo={isHorizontalVideo || false}
          markAsShared={markAsShared}
        />
      )}
    </>
  );
};
